import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModifierSurveyContext } from '../../pages/Modifier'
import { useModifierSurveyStore } from '../../store/useModifierSurveryStore'
import {
  DietKind,
  Electronics,
  Hotels,
  OfficeImprovement,
  OrganicProducts,
  PurchasedGoodsServices,
} from '../../utils/enums/modifierSurveyEnums'
import { Button } from '../core/Button'
import { QuestionWidget } from './QuestionWidget'

export interface SurveyQuestionPurchasedGoodsServicesProps {
  editable: boolean
}

export const SurveyQuestionPurchasedGoodsServices = ({
  editable,
}: SurveyQuestionPurchasedGoodsServicesProps) => {
  const { onPrevTab, onNextTab } = useModifierSurveyContext()
  const { t } = useTranslation(['modifierSurvey'])

  // Purchased Goods Services State
  const [purchasedGoodsValue, setPurchasedGoodsValue] = useState<
    PurchasedGoodsServices | undefined
  >(undefined)
  const purchaseSustainability = useModifierSurveyStore(
    (state) => state.modifierResults?.purchaseSustainability
  )
  const setPurchaseSustainability = useModifierSurveyStore(
    (state) => state.setPurchaseSustainability
  )
  const purchaseSustainabilityText = useModifierSurveyStore(
    (state) => state.modifierResults?.purchaseSustainabilityText
  )
  const setPurchaseSustainabilityText = useModifierSurveyStore(
    (state) => state.setPurchaseSustainabilityText
  )

  // Diet Kind State
  const [dietKindValue, setDietKindValue] = useState<DietKind | undefined>(
    undefined
  )
  const dietKind = useModifierSurveyStore().modifierResults?.dietKind
  const dietKindText = useModifierSurveyStore().modifierResults?.dietKindText
  const setDietKind = useModifierSurveyStore().setDietKind
  const setDietKindText = useModifierSurveyStore().setDietKindText

  console.log(useModifierSurveyStore().modifierResults)

  // Organic Products State
  const [organicProductsValue, setOrganicProductsValue] = useState<
    OrganicProducts | undefined
  >(undefined)
  const organicProducts =
    useModifierSurveyStore().modifierResults?.organicProducts
  const organicProductsText =
    useModifierSurveyStore().modifierResults?.organicProductsText
  const setOrganicProducts = useModifierSurveyStore().setOrganicProducts
  const setOrganicProductsText = useModifierSurveyStore().setOrganicProductsText

  // Office Improvement State
  const [officeImprovementValue, setOfficeImprovementValue] = useState<
    OfficeImprovement | undefined
  >(undefined)
  const officeImprovement =
    useModifierSurveyStore().modifierResults?.officeImprovement
  const officeImprovementText =
    useModifierSurveyStore().modifierResults?.officeImprovementText
  const setOfficeImprovement = useModifierSurveyStore().setOfficeImprovement
  const setOfficeImprovementText =
    useModifierSurveyStore().setOfficeImprovementText

  // Electronics State
  const [electronicsValue, setElectronicsValue] = useState<
    Electronics | undefined
  >(undefined)
  const electronics = useModifierSurveyStore().modifierResults?.electronics
  const electronicsText =
    useModifierSurveyStore().modifierResults?.electronicsText
  const setElectronics = useModifierSurveyStore().setElectronics
  const setElectronicsText = useModifierSurveyStore().setElectronicsText

  // Hotels State
  const [hotelsValue, setHotelsValue] = useState<Hotels | undefined>(undefined)
  const hotels = useModifierSurveyStore().modifierResults?.hotels
  const hotelsText = useModifierSurveyStore().modifierResults?.hotelsText
  const setHotels = useModifierSurveyStore().setHotels
  const setHotelsText = useModifierSurveyStore().setHotelsText

  // Effects to sync state
  useEffect(() => {
    setPurchasedGoodsValue(purchaseSustainability)
  }, [purchaseSustainability])

  useEffect(() => {
    setDietKindValue(dietKind)
  }, [dietKind])

  useEffect(() => {
    setOrganicProductsValue(organicProducts)
  }, [organicProducts])

  useEffect(() => {
    setOfficeImprovementValue(officeImprovement)
  }, [officeImprovement])

  useEffect(() => {
    setElectronicsValue(electronics)
  }, [electronics])

  useEffect(() => {
    setHotelsValue(hotels)
  }, [hotels])

  // Handlers for each section
  const handlePurchasedGoodsSelect = (value: string) => {
    setPurchaseSustainability(value as PurchasedGoodsServices)
  }

  const handleDietKindSelect = (value: string) => {
    setDietKind(value as DietKind)
  }

  const handleOrganicProductsSelect = (value: string) => {
    setOrganicProducts(value as OrganicProducts)
  }

  const handleOfficeImprovementSelect = (value: string) => {
    setOfficeImprovement(value as OfficeImprovement)
  }

  const handleElectronicsSelect = (value: string) => {
    setElectronics(value as Electronics)
  }

  const handleHotelsSelect = (value: string) => {
    setHotels(value as Hotels)
  }

  return (
    <>
      <div className="flex flex-col w-full items-start justify-start col-span-5 pt-8 px-8 py-16 space-y-8">
        {/* Purchased Goods Services */}
        <div className="w-full">
          <QuestionWidget
            options={Object.keys(PurchasedGoodsServices).map((value) => ({
              name: t(`modifierSurvey:purchasedGoodsServices.${value}`),
              value,
            }))}
            selectedValue={purchasedGoodsValue}
            onSelect={handlePurchasedGoodsSelect}
            onCommentInput={setPurchaseSustainabilityText}
            note={purchaseSustainabilityText}
            editable={editable}
          />
        </div>

        {/* Diet Kind */}
        <div className="w-full pt-8">
          <h3 className="text-lg font-medium mb-4">
            {t('modifierSurvey:dietKind.question')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('modifierSurvey:dietKind.explanation')}
          </p>
          <QuestionWidget
            options={Object.keys(DietKind).map((value) => ({
              name: t(`modifierSurvey:dietKind.${value}`),
              value,
            }))}
            selectedValue={dietKindValue}
            onSelect={handleDietKindSelect}
            onCommentInput={setDietKindText}
            note={dietKindText}
            editable={editable}
          />
        </div>

        {/* Organic Products */}
        <div className="w-full pt-8">
          <h3 className="text-lg font-medium mb-4">
            {t('modifierSurvey:organicProducts.question')}
          </h3>
          <QuestionWidget
            options={Object.keys(OrganicProducts).map((value) => ({
              name: t(`modifierSurvey:organicProducts.${value}`),
              value,
            }))}
            selectedValue={organicProductsValue}
            onSelect={handleOrganicProductsSelect}
            onCommentInput={setOrganicProductsText}
            note={organicProductsText}
            editable={editable}
          />
        </div>

        {/* Office Improvement */}
        <div className="w-full pt-8 ">
          <h3 className="text-lg font-medium mb-4">
            {t('modifierSurvey:officeImprovement.question')}
          </h3>
          <QuestionWidget
            options={Object.keys(OfficeImprovement).map((value) => ({
              name: t(`modifierSurvey:officeImprovement.${value}`),
              value,
            }))}
            selectedValue={officeImprovementValue}
            onSelect={handleOfficeImprovementSelect}
            onCommentInput={setOfficeImprovementText}
            note={officeImprovementText}
            editable={editable}
          />
        </div>

        {/* Electronics */}
        <div className="w-full pt-8">
          <h3 className="text-lg font-medium mb-4">
            {t('modifierSurvey:electronics.question')}
          </h3>
          <QuestionWidget
            options={Object.keys(Electronics).map((value) => ({
              name: t(`modifierSurvey:electronics.${value}`),
              value,
            }))}
            selectedValue={electronicsValue}
            onSelect={handleElectronicsSelect}
            onCommentInput={setElectronicsText}
            note={electronicsText}
            editable={editable}
          />
        </div>

        {/* Hotels */}
        <div className="w-full pt-8">
          <h3 className="text-lg font-medium mb-4">
            {t('modifierSurvey:hotels.question')}
          </h3>
          <QuestionWidget
            options={Object.keys(Hotels).map((value) => ({
              name: t(`modifierSurvey:hotels.${value}`),
              value,
            }))}
            selectedValue={hotelsValue}
            onSelect={handleHotelsSelect}
            onCommentInput={setHotelsText}
            note={hotelsText}
            editable={editable}
          />
        </div>
      </div>

      {editable && (
        <div className="bg-gray-100 py-4 flex items-center justify-between w-full px-8">
          <Button
            onClick={onPrevTab}
            label={t('modifierSurvey:common.backBtn')}
            variant="secondary"
          />
          <Button
            onClick={onNextTab}
            label={t('modifierSurvey:common.nextBtn')}
            variant="primary"
            disabled={
              !purchasedGoodsValue ||
              !dietKindValue ||
              !organicProductsValue ||
              !officeImprovementValue ||
              !electronicsValue ||
              !hotelsValue
            }
          />
        </div>
      )}
    </>
  )
}
